import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Field, Input, Select, Checkbox, SubmitButton, ErrorBanner, CommonIdentityFields
} from "@/components/auth/FormPrimitives";
import LogoUpload from "@/components/auth/LogoUpload";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/utils/api";
import { consumePostAuthRedirect } from "@/utils/postAuthRedirect";

export default function SchoolSignupForm() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    full_name: "", email: "", phone: "", password: "",
    country: "India", timezone: "Asia/Kolkata",
    school_name: "", school_address: "", school_board: "CBSE", logo_path: null,
    accept_terms: false, accept_privacy: false,
  });
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!values.logo_path) {
      setError("Please upload your school logo before continuing.");
      return;
    }
    setLoading(true);
    try {
      await signup("school", values);
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="school-signup-form">
      <CommonIdentityFields values={values} onChange={set} testidPrefix="school-signup" />

      <hr className="border-slate-700/60" />
      <p className="text-xs uppercase tracking-wider text-slate-500">School details</p>

      <Field label="School name" required htmlFor="school-name">
        <Input
          id="school-name"
          testid="school-name"
          required
          value={values.school_name}
          onChange={(e) => set("school_name", e.target.value)}
          placeholder="Greenwood High School"
        />
      </Field>
      <Field label="School address" required htmlFor="school-address">
        <Input
          id="school-address"
          testid="school-address"
          required
          value={values.school_address}
          onChange={(e) => set("school_address", e.target.value)}
          placeholder="Street, City, State, PIN"
        />
      </Field>
      <Field label="Board" required htmlFor="school-board">
        <Select
          id="school-board"
          testid="school-board"
          value={values.school_board}
          onChange={(e) => set("school_board", e.target.value)}
        >
          <option value="CBSE">CBSE</option>
          <option value="ICSE">ICSE</option>
          <option value="State">State Board</option>
          <option value="IB">International Baccalaureate (IB)</option>
          <option value="Other">Other</option>
        </Select>
      </Field>

      <LogoUpload onUploaded={(p) => set("logo_path", p)} testidPrefix="school-logo" />

      <div className="pt-2 space-y-2">
        <Checkbox
          testid="school-accept-terms"
          checked={values.accept_terms}
          onChange={(e) => set("accept_terms", e.target.checked)}
        >
          I agree to the <a href="/terms" target="_blank" className="text-sky-400 hover:underline">Terms of Service</a>
        </Checkbox>
        <Checkbox
          testid="school-accept-privacy"
          checked={values.accept_privacy}
          onChange={(e) => set("accept_privacy", e.target.checked)}
        >
          I have read the <a href="/privacy" target="_blank" className="text-sky-400 hover:underline">Privacy Policy</a>
        </Checkbox>
      </div>

      <ErrorBanner error={error} />
      <SubmitButton loading={loading} testid="school-signup-submit">Create School account</SubmitButton>
    </form>
  );
}
